import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Typography } from "@tento-ui/ui/typography";
import { IconButton } from "@tento-ui/components/icon-button";
import { Tooltip } from "@tento-ui/components/tooltip/tooltip";

/** `bar`, in the dsh register — the turn's footer, on one line.
 *
 * Reading order is the contract's: what you can DO about this turn, then
 * what it cost, then what it did. The pile is LAST in every skin — that is
 * the one ordering fact `thread/slots.ts` owns, and it owns it because the
 * pile is the only one of the three that opens a second surface, and a
 * control that leaves the page belongs at the end of a row.
 *
 * ## The metrics are written, not tabulated
 *
 * The native skin hides `meta` behind a details button and renders it as a
 * key/value grid in a tooltip, because a document has one bar per reply and
 * an expanding row shoves the scrollback. dsh spends the line instead: the
 * VALUES, in order, separated by a middle dot.
 *
 *     1m 51s · 3.6k tokens · 12 rows
 *
 * The keys are dropped and that is the point. "took 1m 51s" and "1m 51s"
 * carry the same fact, and the second one is a third the width — a bar that
 * lists five keyed pairs is a table under every turn. The dots are
 * `aria-hidden` because they are punctuation between values, not values.
 *
 * ## The metrics are invisible until the row is hovered
 *
 * `opacity: 0`, not `display: none`: the row keeps its height, so nothing
 * moves when the reader arrives, and nothing moves when they leave. A turn's
 * cost is a fact you go looking for, and a column of transcripts each
 * carrying its own line of numbers is a column with a second column of
 * numbers running down it.
 *
 * On a surface with no hover there is nothing to arrive — `@media (hover:
 * none)` keeps them up, because "reveal on hover" on a touch device is
 * "never reveal". */
@component({ name: "std-dsh-turn-bar" })
export class DshTurnBar extends LonaWebComponent {
  private $actions: HTMLElement = this.$("actions");
  private $meta: HTMLElement = this.$("meta");
  private $faces: HTMLElement = this.$("faces");

  /** Past this many the run stops carrying a count and starts carrying a
   * width; the remainder becomes a number, honest about being one. */
  static readonly FACE_CAP = 12;

  static makeWith(props: DshTurnBar.Props): DshTurnBar {
    return (DshTurnBar.make() as DshTurnBar).bind(props);
  }

  /** All three regions are `replaceChildren`d on every bind, so a pooled bar
   * arriving from a turn with six faces and two actions renders a turn with
   * none of either. */
  bind(props: DshTurnBar.Props): this {
    this.bindActions(props);
    this.bindMeta(props.meta ?? []);
    this.bindFaces(props);
    return this;
  }

  private bindActions(props: DshTurnBar.Props): void {
    const actions = props.actions ?? [];
    this.$actions.style.display = actions.length === 0 ? "none" : "";
    this.$actions.replaceChildren(
      ...actions.map((action) => {
        const $anchor = document.createElement("span");
        $anchor.className = "anchor";
        const $button = IconButton.make().bindIcon(action.icon, {
          sizePx: 20,
          squarish: true,
          ariaLabel: action.label,
        });
        // `addPointerEvent` ASSIGNS the on* properties rather than stacking
        // listeners, so rebinding a pooled row re-points it instead of
        // firing the previous turn's handler as well.
        if (props.onAction != null) {
          const onAction = props.onAction;
          GESTURE_MANAGER.addPointerEvent($button, {
            onClick: () => onAction(action.id),
          });
        }
        // The label is the accessible name AND the hover tooltip, so an icon
        // button in this family is never unlabelled.
        const $tip = Tooltip.make();
        $tip.setAttribute("position", "bottom");
        $tip.textContent = action.label;
        $anchor.append($button, $tip);
        return $anchor;
      }),
    );
  }

  private bindMeta(meta: DshTurnBar.Meta[]): void {
    this.$meta.style.display = meta.length === 0 ? "none" : "";
    const $nodes: HTMLElement[] = [];
    for (const pair of meta) {
      if ($nodes.length > 0) {
        const $dot = Typography.small("·");
        $dot.className = "small meta-dot";
        $dot.setAttribute("aria-hidden", "true");
        $nodes.push($dot);
      }
      // The KEY is not drawn — see the note above — but it is what the value
      // means, so it becomes the accessible name of the run it labels.
      const $value = Typography.small(pair.value);
      $value.className = "small meta-value";
      $value.setAttribute("aria-label", `${pair.key} ${pair.value}`);
      $nodes.push($value);
    }
    this.$meta.replaceChildren(...$nodes);
  }

  /** The pile, as a run of tone dots rather than as initialled avatars.
   *
   * The native skin spends `std-facepile` and `std-avatar` on this because a
   * document has room for a face to carry a letter. dsh's bar is one 20px
   * line under a narrow column, where a 16px circle with a character in it
   * is the heaviest ink in the whole footer — heavier than the reply it
   * belongs to. What survives the reduction is the thing the pile is FOR: a
   * red dot among green is located before any label is parsed, and the
   * overlap makes the set read as one object with a size rather than as a
   * list to count.
   *
   * One label for one object, on the BUTTON. A stack of coloured circles is
   * nothing at all to a screen reader, and there is nothing to hover — half
   * a 6px dot is not a target — so the names live one click away, behind
   * `onFaces`, which is what the pile is for. */
  private bindFaces(props: DshTurnBar.Props): void {
    const faces = props.faces ?? [];
    this.$faces.style.display = faces.length === 0 ? "none" : "";
    if (faces.length === 0) {
      this.$faces.replaceChildren();
      return;
    }
    const shown = faces.slice(0, DshTurnBar.FACE_CAP);
    const rest = faces.length - shown.length;

    const $button = document.createElement("button");
    $button.type = "button";
    $button.className = "facepile-button";

    for (const face of shown) {
      const $dot = document.createElement("span");
      $dot.className = "face";
      $dot.style.background = DshTurnBar.FACE_TONE[face.tone];
      $dot.setAttribute("aria-hidden", "true");
      $button.append($dot);
    }
    if (rest > 0) {
      const $rest = Typography.small(`+${rest}`);
      $rest.className = "small face-rest";
      $button.append($rest);
    }

    const failed = faces.filter((face) =>
      face.tone === "failed" || face.tone === "declined"
    ).length;
    const label = failed > 0
      ? `${faces.length} actions, ${failed} failed — open`
      : `${faces.length} actions — open`;
    $button.ariaLabel = label;

    const $tip = Tooltip.make();
    $tip.setAttribute("position", "bottom");
    $tip.textContent = label;
    $button.append($tip);

    if (props.onFaces != null) {
      const onFaces = props.onFaces;
      GESTURE_MANAGER.addPointerEvent($button, { onClick: () => onFaces() });
    }
    this.$faces.replaceChildren($button);
  }

  /** Through --fg-*, NOT the raw palette hues: the theme's --blue, --green
   * and --red are SYSTEM colours picked to read on a dark surface, and the
   * --fg-* twins are each mixed toward --text-pri so they stay legible on
   * either. --fg-yellow does NOT resolve on the dark theme, so `declined`
   * writes the mix out by hand as its fallback — an undefined custom
   * property falls to the literal here, not to a related token. */
  private static readonly FACE_TONE: Record<DshTurnBar.Face["tone"], string> = {
    pending: "var(--text-ter, #8b93a7)",
    running: "var(--fg-blue, #3b82f6)",
    done: "var(--fg-green, #2da44e)",
    failed: "var(--fg-red, #e5484d)",
    declined:
      "var(--fg-yellow, color-mix(in srgb, var(--yellow, #d4a72c) 72%," +
      " var(--text-pri, #1f2328) 28%))",
  };

  static $styles = [
    Tooltip.$style,
    css`
      :host {
        display: block;
        width: 100%;
      }
      /* Everything sits left, on the content's own margin: this is a menu
         under the turn, not a right rail. */
      #root {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 2px;
        min-height: 20px;
        min-width: 0;
      }
      #actions {
        display: flex;
        align-items: center;
        gap: 2px;
        flex: none;
      }
      /* The whole run, revealed together. Opacity rather than display, so
         the row keeps its height and nothing moves under the cursor. */
      #meta {
        display: flex;
        align-items: baseline;
        min-width: 0;
        margin-left: 6px;
        opacity: 0;
        transition: opacity 120ms ease;
        overflow: hidden;
      }
      :host(:hover) #meta,
      :host(:focus-within) #meta {
        opacity: 1;
      }
      /* No hover, nothing to arrive: on a touch surface the reveal is a
         permanent hide, so the run is simply up. */
      @media (hover: none) {
        #meta {
          opacity: 1;
        }
      }
      .meta-value {
        flex: none;
        margin: 0;
        --font-size: var(--small-size);
        color: var(--text-ter, #8b93a7);
        white-space: nowrap;
      }
      .meta-dot {
        flex: none;
        margin: 0 10px;
        --font-size: var(--small-size);
        color: var(--text-ter, #8b93a7);
        opacity: 0.6;
      }
      /* LAST, and pushed there. The contract owns this position, not the
         skin — see the note on TurnSlots.Props.bar.faces. */
      #faces {
        display: flex;
        align-items: center;
        flex: none;
        margin-left: auto;
        padding-left: 10px;
      }
      .facepile-button {
        /* Positioned so the pile's one tooltip anchors to the BUTTON rather
           than to a dot inside it, and overflow stated because a clipping
           ancestor is the usual way an anchored tooltip disappears. */
        position: relative;
        overflow: visible;
        display: flex;
        align-items: center;
        gap: 0;
        margin: 0;
        padding: 3px 5px;
        border: 0;
        border-radius: 999px;
        background: transparent;
        cursor: pointer;
      }
      .facepile-button:hover {
        background: var(--bg-hover, color-mix(in srgb, var(--text-pri, #1f2328) 6%, transparent));
      }
      /* The overlap is what makes the run one object. The ring is the
         TURN's surface and not the page's — drawn in the page colour, the
         gaps between dots read as holes punched through to whatever is
         behind the bar. */
      .face {
        flex: none;
        width: 6px;
        height: 6px;
        margin-left: -2px;
        border-radius: 50%;
        box-shadow: 0 0 0 1.5px var(--bg-elevated, var(--bg, #fff));
      }
      .face:first-child {
        margin-left: 0;
      }
      .face-rest {
        flex: none;
        margin: 0 0 0 7px;
        --font-size: var(--turn-fine-size, var(--text-8px));
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        color: var(--text-ter, #8b93a7);
      }
      .anchor {
        position: relative;
        display: inline-flex;
        overflow: visible;
      }
      std-icon-button {
        --std-icon-button-color: var(--text-ter, #8b93a7);
        --std-icon-button-padding: 8px;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root">
      <span id="actions"></span>
      <span id="meta"></span>
      <span id="faces"></span>
    </div>
  `;
}

export namespace DshTurnBar {
  export type Action = {
    /** Passed back to `onAction`; the component never interprets it. */
    id: string;
    /** The accessible name and the hover tooltip. */
    label: string;
    /** Raw SVG — the caller owns its own verbs. */
    icon: string;
  };

  /** One fact about the turn. The KEY is not drawn; it becomes the value's
   * accessible name. See the note on the class. */
  export type Meta = { key: string; value: string };

  /** One action the turn took. No mark and no colour: the tone is a STATUS,
   * and which hue stands for it is this skin's business. */
  export type Face = {
    id: string;
    tone: "pending" | "running" | "done" | "failed" | "declined";
    label: string;
  };

  export type Props = {
    actions?: Action[];
    meta?: Meta[];
    /** What the turn DID, when the composition chose not to spend a row on
     * each one. Empty in the density that shows every call — the run is the
     * substitute for those rows, never a duplicate of them. */
    faces?: Face[];
    onAction?: (id: string) => void;
    onFaces?: () => void;
  };
}
